// src/components/RegionForm.js - REFACTORED FOR BASE64

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField } from '@mui/material';
import { useFormContext } from '../context/FormContext';

// Import specialized components
import RegionMeasurements from './RegionMeasurements';
import RegionBoneQuality from './RegionBoneQuality';
import RegionOptionalSlices from './RegionOptionalSlices';
import RegionImageCropper from './RegionImageCropper';

import { fileToBase64 } from '../utils/fileUtils';

const RegionForm = ({
    reportType = '',
    regionNumber = '',
    regionName = '',
    onRegionDataChange = () => { },
    isFirstSubregion = false
}) => {
    const { state, actions } = useFormContext();

    // Get PDF images from RegionDetailsTab state
    const convertedImages = state.regionDetailsPdfState?.convertedImages || [];

    const getRegionFormState = useMemo(() => {
        return actions.getRegionFormState(regionNumber);
    }, [actions, regionNumber]);

    const [localState, setLocalStateInternal] = useState(getRegionFormState);

    const setLocalState = useCallback((updates) => {
        setLocalStateInternal(updates);
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            actions.updateRegionFormState(regionNumber, localState);
        }, 500);

        return () => {
            clearTimeout(handler);
            // Flush state on unmount
            actions.updateRegionFormState(regionNumber, localState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localState, regionNumber]);

    useEffect(() => {
        const dataToSend = {
            pathology_comments: localState.formData.pathology_comments,
            predefined_pathology_comments: localState.formData.predefined_pathology_comments,
            length_crest: localState.formData.length_crest,
            length_crest_to: localState.formData.length_crest_to,
            length_periapical: localState.formData.length_periapical,
            length_periapical_to: localState.formData.length_periapical_to,
            length_lesion: localState.formData.length_lesion,
            length_incision: localState.formData.length_incision,
            ridge_form: localState.formData.ridge_form,
            length_quality: localState.formData.length_quality,
            undercut: localState.formData.undercut,
            angulation: localState.formData.angulation,
            additional_comments: localState.formData.additional_comments,
            width_crest: localState.formData.width_crest,
            width_option: localState.formData.width_option,
            width_quality: localState.formData.width_quality,
            width_additional_comments: localState.formData.width_additional_comments,
            bone_quality: localState.formData.bone_quality,
            optionalSliceData: localState.optionalSliceData,
            croppedImageFile1: localState.croppedImageFile1,
            croppedImageFile2: localState.croppedImageFile2,
            sliceNumber: localState.formData.sliceNumber,
            length_varies_from: localState.formData.length_varies_from,
            width_2mm: localState.formData.width_2mm,
            width_6mm: localState.formData.width_6mm,
            width_10mm: localState.formData.width_10mm,
            width_apical_quality: localState.formData.width_apical_quality
        };

        const handler = setTimeout(() => {
            onRegionDataChange(regionNumber, dataToSend);
        }, 500); // Debounce with 500ms delay

        return () => {
            clearTimeout(handler);
            // Flush on unmount to ensure data isn't lost if user switches tab quickly
            onRegionDataChange(regionNumber, dataToSend);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localState, regionNumber]);

    // Note: With Base64 approach, no complex useEffect cleanup needed
    // Images are stored as Base64 strings which restore automatically

    const handleFormDataChange = useCallback((field, value) => {
        setLocalState(prev => ({
            ...prev,
            formData: { ...prev.formData, [field]: value }
        }));
    }, [setLocalState]);

    const handlePredefinedCommentsChange = useCallback((field, value) => {
        setLocalState(prev => ({
            ...prev,
            formData: { ...prev.formData, [field]: value }
        }));
    }, [setLocalState]);

    const handleBoneQualityChange = useCallback((boneType, value) => {
        setLocalState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                bone_quality: { ...prev.formData.bone_quality, [boneType]: value }
            }
        }));
    }, [setLocalState]);

    const handleOtherBoneQualityCommentChange = useCallback((boneType, value) => {
        setLocalState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                bone_quality: {
                    ...prev.formData.bone_quality,
                    otherComments: {
                        ...prev.formData.bone_quality.otherComments,
                        [boneType]: value
                    }
                }
            }
        }));
    }, [setLocalState]);

    const handleOptionalSliceChange = useCallback((field, value) => {
        setLocalState(prev => ({
            ...prev,
            optionalSliceData: { ...prev.optionalSliceData, [field]: value }
        }));
    }, [setLocalState]);

    // Handlers for image cropping functionality
    const handleImageIndexChange = useCallback((newIndex) => {
        setLocalState(prev => ({
            ...prev,
            currentImageIndex: newIndex
        }));
    }, [setLocalState]);

    const handleCropChange = useCallback((newCrop) => {
        setLocalState(prev => ({
            ...prev,
            crop: newCrop
        }));
    }, [setLocalState]);

    const handleCropComplete = useCallback((completedCrop) => {
        setLocalState(prev => ({
            ...prev,
            completedCrop: completedCrop
        }));
    }, [setLocalState]);

    // Image handling is now simplified with Base64 approach
    const handleCroppedImageChange = useCallback((imageNumber, imageData) => {
        // imageData is now { base64, name, type } object or null
        if (imageNumber === 1) {
            setLocalState(prev => ({
                ...prev,
                croppedImageFile1: imageData,
                croppedImageUrl1: imageData ? imageData.base64 : null
            }));
        } else if (imageNumber === 2) {
            setLocalState(prev => ({
                ...prev,
                croppedImageFile2: imageData,
                croppedImageUrl2: imageData ? imageData.base64 : null
            }));
        }
    }, [setLocalState]);

    const handleRemoveImage = useCallback((imageNumber) => {
        handleCroppedImageChange(imageNumber, null);
    }, [handleCroppedImageChange]);

    const handleSelectImage = async (imageNumber) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            try {
                const base64 = await fileToBase64(file);
                const fileData = { base64, name: file.name, type: file.type };
                handleCroppedImageChange(imageNumber, fileData);
            } catch (error) {
                console.error("Error converting file to Base64:", error);
            }
        };
        input.click();
    };

    // No refresh function needed with Base64 - images are always available

    return (
        <Box sx={{ p: 2, maxWidth: '1200px', mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                Region {regionNumber} Details
                {regionName && ` - ${regionName}`}
            </Typography>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Report Type: {reportType}
            </Typography>

            <TextField
                label="Slice"
                variant="outlined"
                fullWidth
                value={localState.formData.sliceNumber ?? 'Slice 0 to 00'}
                onChange={(e) => handleFormDataChange('sliceNumber', e.target.value)}
                sx={{ mb: 3 }}
            />

            {/* NEW SECTION FOR FIRST SUBREGION */}
            {isFirstSubregion && (
                <Box sx={{ border: '2px solid black', mb: '20px', p: '20px' }}>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            mb: '15px',
                            borderBottom: '1px solid black',
                            pb: '10px'
                        }}
                    >
                        Header Region Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <TextField
                                name="length_varies_from"
                                label="Length varies from"
                                value={localState.formData.length_varies_from || ''}
                                onChange={(e) => handleFormDataChange('length_varies_from', e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                Buccolingual Width at 2mm, 6mm and 10mm from the crest
                            </Typography>
                        </Box>
                        <Box>
                            <TextField
                                name="width_2mm"
                                label="Width at 2mm"
                                value={localState.formData.width_2mm || ''}
                                onChange={(e) => handleFormDataChange('width_2mm', e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box>
                            <TextField
                                name="width_6mm"
                                label="Width at 6mm"
                                value={localState.formData.width_6mm || ''}
                                onChange={(e) => handleFormDataChange('width_6mm', e.target.value)}
                                fullWidth
                            />
                        </Box>
                        <Box>
                            <TextField
                                name="width_10mm"
                                label="Width at 10mm"
                                value={localState.formData.width_10mm || ''}
                                onChange={(e) => handleFormDataChange('width_10mm', e.target.value)}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </Box>
            )}


            {/* PDF Image Cropping Section */}
            {convertedImages.length > 0 && (
                <RegionImageCropper
                    regionNumber={regionNumber}
                    convertedImages={convertedImages}
                    currentImageIndex={localState.currentImageIndex || 0}
                    crop={localState.crop}
                    completedCrop={localState.completedCrop}
                    croppedImageUrl1={localState.croppedImageUrl1}
                    croppedImageUrl2={localState.croppedImageUrl2}
                    croppedImageFile1={localState.croppedImageFile1}
                    croppedImageFile2={localState.croppedImageFile2}
                    onImageIndexChange={handleImageIndexChange}
                    onCropChange={handleCropChange}
                    onCropComplete={handleCropComplete}
                    onCroppedImageChange={handleCroppedImageChange}
                />
            )}

            <RegionMeasurements
                regionNumber={regionNumber}
                reportType={reportType}
                formData={localState.formData}
                onFormDataChange={handleFormDataChange}
                onPredefinedCommentsChange={handlePredefinedCommentsChange}
                croppedImageFile1={localState.croppedImageFile1}
                croppedImageFile2={localState.croppedImageFile2}
                croppedImageUrl1={localState.croppedImageUrl1}
                croppedImageUrl2={localState.croppedImageUrl2}
                onRemoveImage={handleRemoveImage}
                onSelectImage={handleSelectImage}
            />

            <RegionBoneQuality
                regionNumber={regionNumber}
                reportType={reportType}
                boneQuality={localState.formData.bone_quality}
                onBoneQualityChange={handleBoneQualityChange}
                onOtherCommentChange={handleOtherBoneQualityCommentChange}
            />

            <RegionOptionalSlices
                regionNumber={regionNumber}
                crop={localState.crop}
                completedCrop={localState.completedCrop}
                optionalSliceData={localState.optionalSliceData}
                onCropChange={handleCropChange}
                onCropComplete={handleCropComplete}
                onOptionalSliceChange={handleOptionalSliceChange}
            />
        </Box>
    );
};

RegionForm.propTypes = {
    reportType: PropTypes.string.isRequired,
    regionNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    regionName: PropTypes.string,
    onRegionDataChange: PropTypes.func,
    isFirstSubregion: PropTypes.bool
};

export default RegionForm;